import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { FileText, Eye, Check, X, Filter, Calendar, User, Clock, AlertTriangle, Users, UserPlus, Loader2, Mail, ShieldCheck } from "lucide-react";

interface RemoteWorkRequest {
  id: string;
  destination_country: string;
  start_date: string;
  end_date: string;
  requested_points: number;
  manager_approval_status: string;
  hr_approval_status: string;
  risk_level: string;
  risk_notes: string | null;
  comment: string | null;
  created_at: string;
  employee: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    position: string | null;
  };
}

interface TeamMember {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  position: string | null;
  invitation_status: string;
  profile_id: string | null;
}

interface TeamManagementTabProps {
  managerProfileId: string;
  pendingCount: number;
  onPendingCountChange: (count: number) => void;
}

const TeamManagementTab = ({ managerProfileId, pendingCount, onPendingCountChange }: TeamManagementTabProps) => {
  const [requests, setRequests] = useState<RemoteWorkRequest[]>([]);
  const [filteredRequests, setFilteredRequests] = useState<RemoteWorkRequest[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedRequest, setSelectedRequest] = useState<RemoteWorkRequest | null>(null);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [forceApproving, setForceApproving] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    if (managerProfileId) {
      fetchRequests();
      fetchTeamMembers();
    }
  }, [managerProfileId]);

  useEffect(() => {
    let filtered = requests;
    
    if (statusFilter !== "all") {
      filtered = requests.filter(request => request.manager_approval_status === statusFilter);
    }
    
    setFilteredRequests(filtered);
  }, [requests, statusFilter]);

  const fetchRequests = async () => {
    if (!managerProfileId) return;

    try {
      // Get employees managed by this manager
      const { data: employees, error: empError } = await supabase
        .from('employees')
        .select('id, first_name, last_name, email, position')
        .eq('manager_profile_id', managerProfileId);

      if (empError) throw empError;

      if (!employees || employees.length === 0) {
        setRequests([]);
        setLoading(false);
        onPendingCountChange(0);
        return;
      }

      const employeeIds = employees.map(e => e.id);

      // Get requests for those employees
      const { data: requestsData, error: reqError } = await supabase
        .from('remote_work_requests')
        .select('*')
        .in('employee_id', employeeIds)
        .order('created_at', { ascending: false });

      if (reqError) throw reqError;

      // Map employee info to requests
      const requestsWithEmployees = (requestsData || []).map(req => {
        const emp = employees.find(e => e.id === req.employee_id);
        return {
          ...req,
          employee: emp || {
            id: req.employee_id,
            first_name: 'N/A',
            last_name: 'N/A',
            email: 'N/A',
            position: null
          }
        };
      });

      setRequests(requestsWithEmployees);
      
      // Update pending count
      const pendingRequests = requestsWithEmployees.filter(r => r.manager_approval_status === 'pending');
      onPendingCountChange(pendingRequests.length);
    } catch (error: any) {
      console.error('Error fetching team requests:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de charger les demandes de votre équipe"
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamMembers = async () => {
    if (!managerProfileId) return;

    try {
      // Use SECURITY DEFINER function to bypass RLS
      const { data, error } = await supabase
        .rpc('get_manager_team_members', { p_manager_profile_id: managerProfileId });

      if (error) throw error;
      
      // Map the response to match our interface
      const members = (data || []).map((m: any) => ({
        id: m.id,
        first_name: m.first_name,
        last_name: m.last_name,
        email: m.email,
        position: m.employee_position,
        invitation_status: m.invitation_status,
        profile_id: m.profile_id
      }));
      
      setTeamMembers(members);
    } catch (error: any) {
      console.error('Error fetching team members:', error);
    }
  };

  const handleForceApprove = async (employeeId: string) => {
    setForceApproving(employeeId);
    try {
      const response = await supabase.functions.invoke('force-approve-employee', {
        body: {
          employeeId,
          managerProfileId,
          siteUrl: window.location.origin
        }
      });

      if (response.error) throw response.error;
      
      const result = response.data;
      if (result?.error) {
        throw new Error(result.error);
      }

      toast({
        title: "Compte activé",
        description: "Le compte de l'employé a été activé avec succès. Un email de réinitialisation de mot de passe lui a été envoyé."
      });

      await fetchTeamMembers();
    } catch (error: any) {
      console.error('Error force approving employee:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: error.message || "Impossible d'activer le compte"
      });
    } finally {
      setForceApproving(null);
    }
  };

  const getInvitationStatusBadge = (status: string, hasProfile: boolean) => {
    if (hasProfile) {
      return <Badge className="bg-green-100 text-green-800"><Check className="w-3 h-3 mr-1" />Actif</Badge>;
    }
    switch (status) {
      case 'invitation_envoyee':
        return <Badge variant="secondary" className="bg-blue-100 text-blue-800"><Mail className="w-3 h-3 mr-1" />Invitation envoyée</Badge>;
      case 'pending':
        return <Badge variant="secondary" className="bg-yellow-100 text-yellow-800"><Clock className="w-3 h-3 mr-1" />En attente confirmation</Badge>;
      case 'actif':
        return <Badge className="bg-green-100 text-green-800"><Check className="w-3 h-3 mr-1" />Actif</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const handleApprove = async (requestId: string) => {
    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from('remote_work_requests')
        .update({
          manager_approval_status: 'approved',
          manager_approved_at: new Date().toISOString(),
          manager_approver_id: managerProfileId
        })
        .eq('id', requestId);

      if (error) throw error;

      const request = requests.find(r => r.id === requestId);
      if (request) {
        // Get employee's profile_id and company_id
        const { data: empData, error: empError } = await supabase
          .from('employees')
          .select('profile_id, company_id')
          .eq('id', request.employee.id)
          .single();

        console.log('Employee data for notifications:', empData, 'Error:', empError);

        if (empData?.profile_id) {
          // Notify employee via edge function (reliable - bypasses RLS)
          const { error: notifyEmpError } = await supabase.functions.invoke('send-workflow-notification', {
            body: {
              type: 'manager_approved',
              request_id: requestId,
              recipient_profile_id: empData.profile_id,
              employee_name: `${request.employee.first_name} ${request.employee.last_name}`,
              destination_country: request.destination_country,
              start_date: request.start_date,
              end_date: request.end_date
            }
          });
          if (notifyEmpError) console.error('Failed to notify employee:', notifyEmpError);
        }

        // Notify HR (company admin) via edge function
        if (empData?.company_id) {
          const { data: companyData, error: compError } = await supabase
            .from('companies')
            .select('admin_id')
            .eq('id', empData.company_id)
            .single();

          console.log('Company data for HR notification:', companyData, 'Error:', compError);

          if (companyData?.admin_id) {
            console.log('Sending HR notification to:', companyData.admin_id);
            const { error: notifyHrError } = await supabase.functions.invoke('send-workflow-notification', {
              body: {
                type: 'hr_approval_pending',
                request_id: requestId,
                recipient_profile_id: companyData.admin_id,
                employee_name: `${request.employee.first_name} ${request.employee.last_name}`,
                destination_country: request.destination_country,
                start_date: request.start_date,
                end_date: request.end_date
              }
            });
            if (notifyHrError) console.error('Failed to notify HR:', notifyHrError);
            else console.log('HR notification sent successfully');
          } else {
            console.log('No admin_id found for company');
          }
        } else {
          console.log('No company_id found for employee');
        }
      }

      toast({
        title: "Demande approuvée",
        description: "La demande a été transmise aux RH pour validation finale"
      });

      setIsDetailDialogOpen(false);
      fetchRequests();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: error.message || "Impossible d'approuver la demande"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = async (requestId: string) => {
    if (!rejectionReason.trim()) {
      toast({
        variant: "destructive",
        title: "Motif requis",
        description: "Veuillez indiquer un motif de refus"
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from('remote_work_requests')
        .update({
          manager_approval_status: 'rejected',
          manager_approval_reason: rejectionReason,
          manager_approved_at: new Date().toISOString(),
          manager_approver_id: managerProfileId,
          status: 'rejected'
        })
        .eq('id', requestId);

      if (error) throw error;

      // Notify employee via edge function (reliable - bypasses RLS)
      const request = requests.find(r => r.id === requestId);
      if (request) {
        const { data: empData } = await supabase
          .from('employees')
          .select('profile_id')
          .eq('id', request.employee.id)
          .single();

        if (empData?.profile_id) {
          await supabase.functions.invoke('send-workflow-notification', {
            body: {
              type: 'manager_rejected',
              request_id: requestId,
              recipient_profile_id: empData.profile_id,
              employee_name: `${request.employee.first_name} ${request.employee.last_name}`,
              destination_country: request.destination_country,
              start_date: request.start_date,
              end_date: request.end_date,
              reason: rejectionReason
            }
          });
        }
      }

      toast({
        title: "Demande refusée",
        description: "L'employé a été notifié du refus"
      });

      setIsDetailDialogOpen(false);
      setRejectionReason("");
      fetchRequests();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: error.message || "Impossible de refuser la demande"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getManagerStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="secondary" className="bg-yellow-100 text-yellow-800"><Clock className="w-3 h-3 mr-1" />Manager: En attente</Badge>;
      case 'approved':
        return <Badge className="bg-green-100 text-green-800">✓ Manager: Approuvée</Badge>;
      case 'rejected':
        return <Badge variant="destructive">✗ Manager: Refusée</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getHRStatusBadge = (managerStatus: string, hrStatus: string) => {
    // Only show HR status if manager has approved
    if (managerStatus !== 'approved') return null;
    
    switch (hrStatus) {
      case 'pending':
        return <Badge variant="secondary" className="bg-blue-100 text-blue-800"><Clock className="w-3 h-3 mr-1" />RH: En attente</Badge>;
      case 'approved':
        return <Badge className="bg-green-100 text-green-800">✓ RH: Approuvée</Badge>;
      case 'rejected':
        return <Badge variant="destructive">✗ RH: Refusée</Badge>;
      default:
        return <Badge variant="secondary" className="bg-blue-100 text-blue-800"><Clock className="w-3 h-3 mr-1" />RH: En attente</Badge>;
    }
  };

  const getRiskBadge = (level: string) => {
    switch (level) {
      case 'low':
        return <Badge variant="outline" className="text-green-600 border-green-200">Risque faible</Badge>;
      case 'medium':
        return <Badge variant="outline" className="text-yellow-600 border-yellow-200"><AlertTriangle className="w-3 h-3 mr-1" />Risque moyen</Badge>;
      case 'high':
        return <Badge variant="outline" className="text-red-600 border-red-200"><AlertTriangle className="w-3 h-3 mr-1" />Risque élevé</Badge>;
      default:
        return null;
    }
  };

  const calculateDays = (start: string, end: string) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  };

  const openRequestDetail = (request: RemoteWorkRequest) => {
    setSelectedRequest(request);
    setRejectionReason("");
    setIsDetailDialogOpen(true);
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-muted rounded w-1/4"></div>
            <div className="h-64 bg-muted rounded"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const pendingMembers = teamMembers.filter(m => !m.profile_id);
  const activeMembers = teamMembers.filter(m => m.profile_id);

  return (
    <div className="space-y-6">
      {/* Team Members Section */}
      {pendingMembers.length > 0 && (
        <Card className="border-yellow-200 bg-yellow-50/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-yellow-800">
              <UserPlus className="h-5 w-5" />
              Membres en attente d'activation
            </CardTitle>
            <CardDescription>
              {pendingMembers.length} membre(s) de votre équipe n'ont pas encore activé leur compte
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {pendingMembers.map((member) => (
                <div 
                  key={member.id}
                  className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 bg-background border rounded-lg"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">
                        {member.first_name} {member.last_name}
                      </span>
                      {getInvitationStatusBadge(member.invitation_status, !!member.profile_id)}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">{member.email}</p>
                    {member.position && (
                      <p className="text-sm text-muted-foreground">{member.position}</p>
                    )}
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleForceApprove(member.id)}
                    disabled={forceApproving === member.id}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {forceApproving === member.id ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        Activation...
                      </>
                    ) : (
                      <>
                        <ShieldCheck className="h-4 w-4 mr-1" />
                        Forcer l'activation
                      </>
                    )}
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Active Team Members */}
      {activeMembers.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Votre équipe ({activeMembers.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {activeMembers.map((member) => (
                <div 
                  key={member.id}
                  className="flex items-center gap-3 p-3 border rounded-lg"
                >
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">
                      {member.first_name} {member.last_name}
                    </p>
                    <p className="text-sm text-muted-foreground truncate">{member.position || 'Poste non défini'}</p>
                  </div>
                  {getInvitationStatusBadge(member.invitation_status, !!member.profile_id)}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Remote Work Requests Section */}
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Demandes de votre équipe
              </CardTitle>
              <CardDescription>
                {requests.filter(r => r.manager_approval_status === 'pending').length} demande(s) en attente de votre validation
              </CardDescription>
            </div>
            
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes</SelectItem>
                  <SelectItem value="pending">En attente</SelectItem>
                  <SelectItem value="approved">Approuvées</SelectItem>
                  <SelectItem value="rejected">Refusées</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        
        <CardContent>
          {filteredRequests.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                {requests.length === 0 ? "Aucune demande de votre équipe" : "Aucune demande ne correspond aux filtres"}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredRequests.map((request) => (
                <div 
                  key={request.id} 
                  className={`p-4 md:p-6 border rounded-lg transition-all hover:shadow-md ${
                    request.manager_approval_status === 'pending' ? 'border-yellow-200 bg-yellow-50/50' :
                    request.manager_approval_status === 'approved' ? 'border-green-200 bg-green-50/50' :
                    'border-red-200 bg-red-50/50'
                  }`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="flex-1 space-y-3">
                      {/* Employee & Status Row */}
                      <div className="flex flex-wrap items-center gap-2">
                        <User className="h-4 w-4 text-muted-foreground" />
                        <span className="font-semibold">
                          {request.employee.first_name} {request.employee.last_name}
                        </span>
                        <span className="text-muted-foreground">•</span>
                        <span className="text-sm text-muted-foreground">
                          {request.employee.position || 'Poste non défini'}
                        </span>
                        {getManagerStatusBadge(request.manager_approval_status)}
                        {getHRStatusBadge(request.manager_approval_status, request.hr_approval_status)}
                      </div>

                      {/* Destination & Dates */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
                        <div>
                          <span className="text-muted-foreground">Destination:</span>
                          <p className="font-medium">{request.destination_country}</p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Période:</span>
                          <p className="font-medium">
                            {new Date(request.start_date).toLocaleDateString('fr-FR')} - {new Date(request.end_date).toLocaleDateString('fr-FR')}
                          </p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Durée:</span>
                          <p className="font-medium">{calculateDays(request.start_date, request.end_date)} jours</p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Points:</span>
                          <p className="font-medium">{request.requested_points} 🪺</p>
                        </div>
                      </div>

                      {/* Risk Assessment */}
                      {request.risk_level && (
                        <div className="flex items-center gap-2">
                          {getRiskBadge(request.risk_level)}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col sm:flex-row gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openRequestDetail(request)}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        Détails
                      </Button>
                      
                      {request.manager_approval_status === 'pending' && (
                        <>
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => handleApprove(request.id)}
                            disabled={isSubmitting}
                          >
                            <Check className="h-4 w-4 mr-1" />
                            Approuver
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => openRequestDetail(request)}
                          >
                            <X className="h-4 w-4 mr-1" />
                            Refuser
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Request Detail Dialog */}
      <Dialog open={isDetailDialogOpen} onOpenChange={setIsDetailDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Détails de la demande
            </DialogTitle>
            <DialogDescription>
              Demande de {selectedRequest?.employee.first_name} {selectedRequest?.employee.last_name}
            </DialogDescription>
          </DialogHeader>

          {selectedRequest && (
            <div className="space-y-6">
              {/* Employee Info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg">
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Employé</Label>
                  <p className="font-medium">{selectedRequest.employee.first_name} {selectedRequest.employee.last_name}</p>
                  <p className="text-sm text-muted-foreground">{selectedRequest.employee.email}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Poste</Label>
                  <p className="font-medium">{selectedRequest.employee.position || 'Non spécifié'}</p>
                </div>
              </div>

              {/* Request Details */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Pays de destination</Label>
                  <p className="font-medium">{selectedRequest.destination_country}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Points demandés</Label>
                  <p className="font-medium">{selectedRequest.requested_points} points 🪺</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Date de début</Label>
                  <p className="font-medium">{new Date(selectedRequest.start_date).toLocaleDateString('fr-FR')}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Date de fin</Label>
                  <p className="font-medium">{new Date(selectedRequest.end_date).toLocaleDateString('fr-FR')}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Durée totale</Label>
                  <p className="font-medium">{calculateDays(selectedRequest.start_date, selectedRequest.end_date)} jours</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Demandé le</Label>
                  <p className="font-medium">{new Date(selectedRequest.created_at).toLocaleDateString('fr-FR')}</p>
                </div>
              </div>

              {/* Workflow Status */}
              <div className="p-4 border rounded-lg space-y-2">
                <Label className="text-sm font-medium text-muted-foreground">Statut du workflow</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {getManagerStatusBadge(selectedRequest.manager_approval_status)}
                  {getHRStatusBadge(selectedRequest.manager_approval_status, selectedRequest.hr_approval_status)}
                </div>
              </div>

              <div className="p-4 border rounded-lg">
                <Label className="text-sm font-medium text-muted-foreground">Évaluation des risques</Label>
                <div className="mt-2">
                  {getRiskBadge(selectedRequest.risk_level || 'low')}
                  {selectedRequest.risk_notes && (
                    <p className="text-sm mt-2 text-muted-foreground">{selectedRequest.risk_notes}</p>
                  )}
                </div>
              </div>

              {/* Comment */}
              {selectedRequest.comment && (
                <div className="p-4 border rounded-lg">
                  <Label className="text-sm font-medium text-muted-foreground">Commentaire de l'employé</Label>
                  <p className="mt-2">{selectedRequest.comment}</p>
                </div>
              )}

              {/* Rejection Reason Input (only for pending) */}
              {selectedRequest.manager_approval_status === 'pending' && (
                <div className="space-y-2">
                  <Label htmlFor="rejection-reason">Motif de refus (obligatoire pour refuser)</Label>
                  <Textarea
                    id="rejection-reason"
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Indiquez le motif du refus..."
                    rows={3}
                  />
                </div>
              )}
            </div>
          )}

          {selectedRequest?.manager_approval_status === 'pending' && (
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button
                variant="outline"
                onClick={() => setIsDetailDialogOpen(false)}
              >
                Annuler
              </Button>
              <Button
                variant="destructive"
                onClick={() => handleReject(selectedRequest.id)}
                disabled={isSubmitting || !rejectionReason.trim()}
              >
                <X className="h-4 w-4 mr-1" />
                Refuser
              </Button>
              <Button
                className="bg-green-600 hover:bg-green-700"
                onClick={() => handleApprove(selectedRequest.id)}
                disabled={isSubmitting}
              >
                <Check className="h-4 w-4 mr-1" />
                Approuver
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamManagementTab;
